import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useUserStore } from '@/stores/user'
import {
  ACCOUNT_TYPE_ACTIVITY,
  ACCOUNT_TYPE_GIFT,
  ACCOUNT_TYPE_PURCHASE,
  ACCOUNT_TYPE_VIP,
  WALLET_TYPE_COIN,
  WALLET_TYPE_GROWTH,
  walletApi,
  type UserWalletAccount,
  type UserWalletTypeSummary,
} from '@/api/wallet'

/** 潮币子账户本地增减增量，未传字段视为 0 */
export interface WalletCoinAdjustDelta {
  coin_purchase?: number
  coin_activity?: number
  coin_gift?: number
  coin_vip?: number
}

const parseBalanceAmount = (value?: string | null): number => {
  if (value == null) return 0
  const text = String(value).trim()
  if (!text) return 0
  const parsed = Number.parseFloat(text)
  return Number.isFinite(parsed) ? parsed : 0
}

const findWalletTypeSummaryBalance = (
  summaries: UserWalletTypeSummary[] | undefined,
  walletType: number
): number => {
  if (!Array.isArray(summaries)) return 0
  const item = summaries.find((summary) => summary.walletType === walletType)
  return parseBalanceAmount(item?.balanceAmount)
}

const findAccountBalance = (
  accounts: UserWalletAccount[] | undefined,
  walletType: number,
  accountType: number
): number => {
  if (!Array.isArray(accounts)) return 0
  const item = accounts.find(
    (account) => account.walletType === walletType && account.accountType === accountType
  )
  return parseBalanceAmount(item?.balanceAmount)
}

export const useWalletStore = defineStore('wallet', () => {
  const coin = ref(0)
  const growth = ref(0)
  const coin_purchase = ref(0)
  const coin_activity = ref(0)
  const coin_gift = ref(0)
  const coin_vip = ref(0)

  let inflight: Promise<boolean> | null = null

  const hasValidRefreshToken = () => {
    const userStore = useUserStore()
    const refreshToken = userStore.userInfo?.refreshToken || localStorage.getItem('refreshToken')
    return userStore.isLoggedIn && !!refreshToken
  }

  const reset = () => {
    inflight = null
    coin.value = 0
    growth.value = 0
    coin_purchase.value = 0
    coin_activity.value = 0
    coin_gift.value = 0
    coin_vip.value = 0
  }

  const applyAccounts = (summaries: UserWalletTypeSummary[], accounts: UserWalletAccount[]) => {
    coin.value = findWalletTypeSummaryBalance(summaries, WALLET_TYPE_COIN)
    growth.value = findWalletTypeSummaryBalance(summaries, WALLET_TYPE_GROWTH)
    coin_purchase.value = findAccountBalance(accounts, WALLET_TYPE_COIN, ACCOUNT_TYPE_PURCHASE)
    coin_activity.value = findAccountBalance(accounts, WALLET_TYPE_COIN, ACCOUNT_TYPE_ACTIVITY)
    coin_gift.value = findAccountBalance(accounts, WALLET_TYPE_COIN, ACCOUNT_TYPE_GIFT)
    coin_vip.value = findAccountBalance(accounts, WALLET_TYPE_COIN, ACCOUNT_TYPE_VIP)
  }

  /** 已登录且刷新令牌有效时拉取钱包余额；未登录不发请求 */
  const fetchAccounts = async (force = false): Promise<boolean> => {
    if (!hasValidRefreshToken()) {
      return false
    }
    if (inflight) {
      if (!force) {
        return inflight
      }
      await inflight
      return fetchAccounts(false)
    }

    inflight = (async () => {
      try {
        const res = await walletApi.queryAccounts()
        if (!hasValidRefreshToken()) {
          return false
        }
        if (res?.code !== 0 || !res.data) {
          console.error('[walletStore] 查询钱包余额失败:', res?.message)
          return false
        }
        applyAccounts(res.data.walletTypeSummaries, res.data.accounts)
        return true
      } catch (error) {
        console.error('[walletStore] 查询钱包余额失败:', error)
        return false
      } finally {
        inflight = null
      }
    })()

    return inflight
  }

  /** 本地增减充值 / 活动 / 投币 / VIP 余额，并同步增减总潮币 */
  const adjustCoin = (delta: WalletCoinAdjustDelta) => {
    const purchase = delta.coin_purchase ?? 0
    const activity = delta.coin_activity ?? 0
    const gift = delta.coin_gift ?? 0
    const vip = delta.coin_vip ?? 0
    coin_purchase.value += purchase
    coin_activity.value += activity
    coin_gift.value += gift
    coin_vip.value += vip
    coin.value += purchase + activity + gift + vip
  }

  return {
    coin,
    growth,
    coin_purchase,
    coin_activity,
    coin_gift,
    coin_vip,
    fetchAccounts,
    adjustCoin,
    reset,
  }
})
